import React from "react";
import ToolsPanel from "./chat/ToolsPanel";
import {
  RefreshCw,
  ExternalLink,
  Eye,
  Code,
  Database,
  TvMinimalPlay,
  FolderOpen,
  Cog,
} from "lucide-react";
import PublishMenu from "./project/PublishMenu";

const MobileToolsSubheader = ({
  sandboxHost,
  onRefresh,
  onOpenInNewWindow,
  viewMode,
  onViewModeChange,
  workspace,
  onOpenProjectSettings,
  mobileToolOverlay,
  setMobileToolOverlay,
  workspaceDeployedUrl,
  toolsPanelRef,
  logs,
  terminalTabs,
  activeTerminalTab,
  setActiveTerminalTab,
  setTerminalTabs,
  agentStatus,
  logRef,
  terminalRef,
  messages,
  setInput,
  handleProgrammaticSubmit,
}) => {
  const handleViewModeClick = (mode) => {
    // Always call onViewModeChange to match desktop behavior
    onViewModeChange(mode);
    
    if (mobileToolOverlay === mode) {
      // If clicking the same mode, close overlay
      setMobileToolOverlay(null);
    } else {
      // Open overlay with selected tool
      setMobileToolOverlay(mode);
    }
  };

  const handleCloseOverlay = () => {
    setMobileToolOverlay(null);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Overlay Header - only show for preview */}
      {mobileToolOverlay === 'preview' && (
        <div className="flex items-center justify-between p-2 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
          <div className="flex items-center gap-1 flex-1">
            {/* Refresh button */}
            <button
              className="p-1 text-ui-text-secondary hover:text-ui-text-primary hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded transition-colors flex-shrink-0"
              onClick={() => {
                // Trigger refresh for preview panel
                if (toolsPanelRef.current?.refreshPreview) {
                  toolsPanelRef.current.refreshPreview();
                }
              }}
              title="Refresh"
            >
              <RefreshCw size={14} />
            </button>

            {/* URL input field */}
            <div className="flex-1 mx-1">
              <input
                type="text"
                value={workspace?.sandbox_host ? `https://${workspace.sandbox_host}` : ''}
                placeholder="Sandbox environment not ready"
                className="w-full px-2 py-1 text-xs bg-neutral-50 dark:bg-neutral-900 rounded border border-neutral-200 dark:border-neutral-600 text-ui-text-primary cursor-default"
                readOnly
              />
            </div>

            <button
              className={`p-1 text-ui-text-secondary hover:text-ui-text-primary hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded transition-colors flex-shrink-0 ${!workspace?.sandbox_host ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={onOpenInNewWindow}
              title="Open in New Window"
              disabled={!workspace?.sandbox_host}
              aria-disabled={!workspace?.sandbox_host}
            >
              <ExternalLink size={14} />
            </button>
          </div>
          <button
            className="p-1 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors flex-shrink-0 ml-1"
            onClick={handleCloseOverlay}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-neutral-600 dark:text-neutral-400">
              <path d="M18 6 6 18"></path>
              <path d="m6 6 12 12"></path>
            </svg>
          </button>
        </div>
      )}

      {/* Overlay Content */}
      <div className="flex-1 overflow-hidden">
        <ToolsPanel
          ref={toolsPanelRef}
          selectedToolTab={mobileToolOverlay}
          setSelectedToolTab={setMobileToolOverlay}
          logs={logs}
          terminalTabs={terminalTabs}
          activeTerminalTab={activeTerminalTab}
          setActiveTerminalTab={setActiveTerminalTab}
          setTerminalTabs={setTerminalTabs}
          agentStatus={agentStatus}
          logRef={logRef}
          terminalRef={terminalRef}
          splitWidth={100}
          messages={messages}
          sandboxHost={sandboxHost}
          workspaceId={workspace?.id}
          workspace={workspace}
          setInput={setInput}
          handleProgrammaticSubmit={handleProgrammaticSubmit}
          hideHeader={true}
        />
      </div>
    </div>
  );
};

const MobileToolsSubheaderButtons = ({
  workspace,
  mobileToolOverlay,
  handleViewModeClick,
  onOpenProjectSettings,
  workspaceDeployedUrl,
}) => {
  return (
    <div className="lg:hidden bg-slate-300 dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 text-neutral-900 dark:text-white py-1 px-2 sm:px-4 flex items-center justify-between shadow-sm z-20 w-full transition-all duration-200">
      {/* Tool Mode Buttons */}
        <div className="flex bg-neutral-100 dark:bg-neutral-700 rounded-lg p-0.5 flex-1 max-w-md">
        {/* Show preview button only if project_mode is 'vibe' */}
        {workspace?.project_mode === "vibe" && (
          <button
            className={`flex-1 px-2 py-1.5 text-sm font-medium rounded transition-colors flex items-center justify-center ${
              mobileToolOverlay === "preview"
                ? "bg-white dark:bg-neutral-800 text-ui-text-primary shadow-sm"
                : "text-ui-text-secondary hover:text-ui-text-primary"
            }`}
            onClick={() => handleViewModeClick("preview")}
            title="Preview"
          >
            <Eye size={16} className="mr-1" />
            <span className="hidden sm:inline">Preview</span>
          </button>
        )}

        {/* Always show monitor button */}
        <button
          className={`flex-1 px-2 py-1.5 text-sm font-medium rounded transition-colors flex items-center justify-center ${
            mobileToolOverlay === "monitor"
              ? "bg-white dark:bg-neutral-800 text-ui-text-primary shadow-sm"
              : "text-ui-text-secondary hover:text-ui-text-primary"
          }`}
          onClick={() => handleViewModeClick("monitor")}
          title="Monitor"
        >
          <TvMinimalPlay size={16} className="mr-1" />
          <span className="hidden sm:inline">Monitor</span>
        </button>

        <button
          className={`flex-1 px-2 py-1.5 text-sm font-medium rounded transition-colors flex items-center justify-center ${
            mobileToolOverlay === "code"
              ? "bg-white dark:bg-neutral-800 text-ui-text-primary shadow-sm"
              : "text-ui-text-secondary hover:text-ui-text-primary"
          }`}
          onClick={() => handleViewModeClick("code")}
          title="Code"
        >
          <Code size={16} className="mr-1" />
          <span className="hidden sm:inline">Files</span>
        </button>

        <button
          className={`flex-1 px-2 py-1.5 text-sm font-medium rounded transition-colors flex items-center justify-center ${
            mobileToolOverlay === "database"
              ? "bg-white dark:bg-neutral-800 text-ui-text-primary shadow-sm"
              : "text-ui-text-secondary hover:text-ui-text-primary"
          }`}
          onClick={() => handleViewModeClick("database")}
          title="Database"
        >
          <Database size={16} className="mr-1" />
          <span className="hidden sm:inline">Collections</span>
        </button>

        {/* Files (Project) view */}
        <button
          className={`flex-1 px-2 py-1.5 text-sm font-medium rounded transition-colors flex items-center justify-center ${
            mobileToolOverlay === "project_files"
              ? "bg-white dark:bg-neutral-800 text-ui-text-primary shadow-sm"
              : "text-ui-text-secondary hover:text-ui-text-primary"
          }`}
          onClick={() => handleViewModeClick("project_files")}
          title="Project Files"
        >
          <FolderOpen size={16} className="mr-1" />
          <span className="hidden sm:inline">Project Files</span>
        </button>

        {/* Project Settings (Engine) */}
        <button
          className="flex-1 px-2 py-1.5 text-sm font-medium rounded transition-colors flex items-center justify-center text-ui-text-secondary hover:text-ui-text-primary"
          onClick={onOpenProjectSettings}
          title="Project Settings"
        >
          <Cog size={16} className="mr-1" />
          <span className="hidden sm:inline">Project Settings</span>
        </button>
      </div>

      {/* Right Side Actions - Publish Button */}
      <div className="flex items-center space-x-1 ml-2">
        {workspace?.id && (
          <PublishMenu
            workspaceId={workspace.id}
            deployedUrl={workspaceDeployedUrl}
            templateSlug={workspace?.template_slug}
          />
        )}
      </div>
    </div>
  );
};

export { MobileToolsSubheaderButtons };
export default MobileToolsSubheader;
